import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { getGeneralSettings, updateGeneralSettings } from "@/lib/settingsApi";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

type Settings = {
  id: string;
  general_terms: string | null;
  payment_terms: string | null;
  privacy_policy: string | null;
  expiration_days: number | null;
  in_progress_to_sent_sla_hours: number | null;
};

const GeneralSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<Settings | null>(null);
  const { toast } = useToast();
  const { register, handleSubmit, reset } = useForm();

  const fetchSettings = async () => {
    try {
      const data = await getGeneralSettings();
      setSettings(data);
      reset({
        general_terms: data.general_terms || "",
        payment_terms: data.payment_terms || "",
        privacy_policy: data.privacy_policy || "",
        expiration_days: data.expiration_days || 30,
        in_progress_to_sent_sla_hours: data.in_progress_to_sent_sla_hours || 24,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const onSubmit = async (data: any) => {
    try {
      await updateGeneralSettings({
        general_terms: data.general_terms,
        payment_terms: data.payment_terms,
        privacy_policy: data.privacy_policy,
        expiration_days: parseInt(data.expiration_days) || 30,
        in_progress_to_sent_sla_hours: parseInt(data.in_progress_to_sent_sla_hours) || 24,
      });

      toast({
        title: "Success",
        description: "Settings updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">General Settings</h2>
        <p className="text-sm text-muted-foreground">Configure general terms and policies</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>General Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register("general_terms")}
              rows={6}
              className="w-full"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Terms</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register("payment_terms")}
              rows={6}
              className="w-full"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register("privacy_policy")}
              rows={6}
              className="w-full"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Proposal Expiration</CardTitle>
            <CardDescription>Set the number of days until proposals expire after being sent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="expiration_days">Expiration Days</Label>
              <Input
                id="expiration_days"
                type="number"
                min="1"
                max="365"
                {...register("expiration_days")}
                className="w-full max-w-xs"
              />
              <p className="text-sm text-muted-foreground">
                Proposals will expire this many days after being sent to clients
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sales SLA</CardTitle>
            <CardDescription>Set the SLA (in hours) to move proposals from In Progress to Sent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="in_progress_to_sent_sla_hours">SLA Hours (In Progress → Sent)</Label>
              <Input
                id="in_progress_to_sent_sla_hours"
                type="number"
                min="1"
                max="720"
                {...register("in_progress_to_sent_sla_hours")}
                className="w-full max-w-xs"
              />
              <p className="text-sm text-muted-foreground">
                This value is used as due date in dashboard cards while proposal status is In Progress
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => reset()}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </div>
  );
};

export default GeneralSettings;
